import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Enums from 'Models/Enums';

export interface IPriceEntityAttributes extends IModelAttributes {
	amount: number;
	mode?: Enums.priceMode;

	endStopId?: string;
	endStop?: Models.StopEntity | Models.IStopEntityAttributes;
	defaultPricingId?: string;
	defaultPricing?: Models.DefaultPricingEntity | Models.IDefaultPricingEntityAttributes;
	ferryTripId?: string;
	ferryTrip?: Models.FerryTripEntity | Models.IFerryTripEntityAttributes;
	measurementId?: string;
	measurement?: Models.MeasurementEntity | Models.IMeasurementEntityAttributes;
	trailerMeasurementId?: string;
	trailerMeasurement?: Models.MeasurementEntity | Models.IMeasurementEntityAttributes;
	routeId?: string;
	route?: Models.RouteEntity | Models.IRouteEntityAttributes;
	userId?: string;
	user?: Models.UserEntity | Models.IUserEntityAttributes;
	freightPriceRangeId?: string;
	freightPriceRange?: Models.FreightPriceRangeEntity | Models.IFreightPriceRangeEntityAttributes;
	startStopId?: string;
	startStop?: Models.StopEntity | Models.IStopEntityAttributes;
	passengerTypeId?: string;
	passengerType?: Models.PassengerTypeEntity | Models.IPassengerTypeEntityAttributes;
	/**
	 * Set only on a class-specific fare. A row carrying one is the fare for that (passenger type,
	 * class) pair; a row without one is the ordinary fare for the type, which every class with no
	 * row of its own still falls back to.
	 */
	passengerClassId?: string;
	pricingStrategyId?: string;
	pricingStrategy?: Models.PricingStrategyEntity | Models.IPricingStrategyEntityAttributes;
	freightTypeId?: string;
	freightType?: Models.FreightTypeEntity | Models.IFreightTypeEntityAttributes;
}
@entity('PriceEntity', 'Price')
export default class PriceEntity extends Model implements IPriceEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<PriceEntity, number>()
	public amount: number;

	@observable
	@attribute()
	public mode: Enums.priceMode = 'FIXED';

	@observable
	@attribute()
	public endStopId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public endStop: Models.StopEntity;

	@observable
	@attribute()
	public defaultPricingId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public defaultPricing: Models.DefaultPricingEntity;

	@observable
	@attribute()
	public ferryTripId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public ferryTrip: Models.FerryTripEntity;

	@observable
	@attribute()
	public measurementId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public measurement: Models.MeasurementEntity;

	@observable
	@attribute()
	public trailerMeasurementId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public trailerMeasurement: Models.MeasurementEntity;

	@observable
	@attribute()
	public routeId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public route: Models.RouteEntity;

	@observable
	@attribute()
	public userId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public user: Models.UserEntity;

	@observable
	@attribute()
	public freightPriceRangeId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public freightPriceRange: Models.FreightPriceRangeEntity;

	@observable
	@attribute()
	public startStopId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public startStop: Models.StopEntity;

	@observable
	@attribute()
	public passengerTypeId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public passengerType: Models.PassengerTypeEntity;

	@observable
	@attribute()
	public passengerClassId?: string;

	@observable
	@attribute()
	public pricingStrategyId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public pricingStrategy: Models.PricingStrategyEntity;

	@observable
	@attribute()
	public freightTypeId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public freightType: Models.FreightTypeEntity;

	constructor(attributes?: Partial<IPriceEntityAttributes>) {
		super(attributes);

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IPriceEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.amount !== undefined) {
				this.amount = attributes.amount;
			}
			if (attributes.mode !== undefined) {
				this.mode = attributes.mode;
			}
			if (attributes.endStopId !== undefined) {
				this.endStopId = attributes.endStopId;
			}
			if (attributes.endStop !== undefined) {
				if (attributes.endStop === null) {
					this.endStop = attributes.endStop;
				} else if (attributes.endStop instanceof Models.StopEntity) {
					this.endStop = attributes.endStop;
					this.endStopId = attributes.endStop.id;
				} else {
					this.endStop = new Models.StopEntity(attributes.endStop);
					this.endStopId = this.endStop.id;
				}
			}
			if (attributes.defaultPricingId !== undefined) {
				this.defaultPricingId = attributes.defaultPricingId;
			}
			if (attributes.defaultPricing !== undefined) {
				if (attributes.defaultPricing === null) {
					this.defaultPricing = attributes.defaultPricing;
				} else if (attributes.defaultPricing instanceof Models.DefaultPricingEntity) {
					this.defaultPricing = attributes.defaultPricing;
					this.defaultPricingId = attributes.defaultPricing.id;
				} else {
					this.defaultPricing = new Models.DefaultPricingEntity(attributes.defaultPricing);
					this.defaultPricingId = this.defaultPricing.id;
				}
			}
			if (attributes.ferryTripId !== undefined) {
				this.ferryTripId = attributes.ferryTripId;
			}
			if (attributes.ferryTrip !== undefined) {
				if (attributes.ferryTrip === null) {
					this.ferryTrip = attributes.ferryTrip;
				} else if (attributes.ferryTrip instanceof Models.FerryTripEntity) {
					this.ferryTrip = attributes.ferryTrip;
					this.ferryTripId = attributes.ferryTrip.id;
				} else {
					this.ferryTrip = new Models.FerryTripEntity(attributes.ferryTrip);
					this.ferryTripId = this.ferryTrip.id;
				}
			}
			if (attributes.measurementId !== undefined) {
				this.measurementId = attributes.measurementId;
			}
			if (attributes.measurement !== undefined) {
				if (attributes.measurement === null) {
					this.measurement = attributes.measurement;
				} else if (attributes.measurement instanceof Models.MeasurementEntity) {
					this.measurement = attributes.measurement;
					this.measurementId = attributes.measurement.id;
				} else {
					this.measurement = new Models.MeasurementEntity(attributes.measurement);
					this.measurementId = this.measurement.id;
				}
			}
			if (attributes.trailerMeasurementId !== undefined) {
				this.trailerMeasurementId = attributes.trailerMeasurementId;
			}
			if (attributes.trailerMeasurement !== undefined) {
				if (attributes.trailerMeasurement === null) {
					this.trailerMeasurement = attributes.trailerMeasurement;
				} else if (attributes.trailerMeasurement instanceof Models.MeasurementEntity) {
					this.trailerMeasurement = attributes.trailerMeasurement;
					this.trailerMeasurementId = attributes.trailerMeasurement.id;
				} else {
					this.trailerMeasurement = new Models.MeasurementEntity(attributes.trailerMeasurement);
					this.trailerMeasurementId = this.trailerMeasurement.id;
				}
			}
			if (attributes.routeId !== undefined) {
				this.routeId = attributes.routeId;
			}
			if (attributes.route !== undefined) {
				if (attributes.route === null) {
					this.route = attributes.route;
				} else if (attributes.route instanceof Models.RouteEntity) {
					this.route = attributes.route;
					this.routeId = attributes.route.id;
				} else {
					this.route = new Models.RouteEntity(attributes.route);
					this.routeId = this.route.id;
				}
			}
			if (attributes.userId !== undefined) {
				this.userId = attributes.userId;
			}
			if (attributes.user !== undefined) {
				if (attributes.user === null) {
					this.user = attributes.user;
				} else if (attributes.user instanceof Models.UserEntity) {
					this.user = attributes.user;
					this.userId = attributes.user.id;
				} else {
					this.user = new Models.UserEntity(attributes.user);
					this.userId = this.user.id;
				}
			}
			if (attributes.freightPriceRangeId !== undefined) {
				this.freightPriceRangeId = attributes.freightPriceRangeId;
			}
			if (attributes.freightPriceRange !== undefined) {
				if (attributes.freightPriceRange === null) {
					this.freightPriceRange = attributes.freightPriceRange;
				} else if (attributes.freightPriceRange instanceof Models.FreightPriceRangeEntity) {
					this.freightPriceRange = attributes.freightPriceRange;
					this.freightPriceRangeId = attributes.freightPriceRange.id;
				} else {
					this.freightPriceRange = new Models.FreightPriceRangeEntity(attributes.freightPriceRange);
					this.freightPriceRangeId = this.freightPriceRange.id;
				}
			}
			if (attributes.startStopId !== undefined) {
				this.startStopId = attributes.startStopId;
			}
			if (attributes.startStop !== undefined) {
				if (attributes.startStop === null) {
					this.startStop = attributes.startStop;
				} else if (attributes.startStop instanceof Models.StopEntity) {
					this.startStop = attributes.startStop;
					this.startStopId = attributes.startStop.id;
				} else {
					this.startStop = new Models.StopEntity(attributes.startStop);
					this.startStopId = this.startStop.id;
				}
			}
			if (attributes.passengerTypeId !== undefined) {
				this.passengerTypeId = attributes.passengerTypeId;
			}
			if (attributes.passengerType !== undefined) {
				if (attributes.passengerType === null) {
					this.passengerType = attributes.passengerType;
				} else if (attributes.passengerType instanceof Models.PassengerTypeEntity) {
					this.passengerType = attributes.passengerType;
					this.passengerTypeId = attributes.passengerType.id;
				} else {
					this.passengerType = new Models.PassengerTypeEntity(attributes.passengerType);
					this.passengerTypeId = this.passengerType.id;
				}
			}
			if (attributes.passengerClassId !== undefined) {
				this.passengerClassId = attributes.passengerClassId;
			}
			if (attributes.pricingStrategyId !== undefined) {
				this.pricingStrategyId = attributes.pricingStrategyId;
			}
			if (attributes.pricingStrategy !== undefined) {
				if (attributes.pricingStrategy === null) {
					this.pricingStrategy = attributes.pricingStrategy;
				} else if (attributes.pricingStrategy instanceof Models.PricingStrategyEntity) {
					this.pricingStrategy = attributes.pricingStrategy;
					this.pricingStrategyId = attributes.pricingStrategy.id;
				} else {
					this.pricingStrategy = new Models.PricingStrategyEntity(attributes.pricingStrategy);
					this.pricingStrategyId = this.pricingStrategy.id;
				}
			}
			if (attributes.freightTypeId !== undefined) {
				this.freightTypeId = attributes.freightTypeId;
			}
			if (attributes.freightType !== undefined) {
				if (attributes.freightType === null) {
					this.freightType = attributes.freightType;
				} else if (attributes.freightType instanceof Models.FreightTypeEntity) {
					this.freightType = attributes.freightType;
					this.freightTypeId = attributes.freightType.id;
				} else {
					this.freightType = new Models.FreightTypeEntity(attributes.freightType);
					this.freightTypeId = this.freightType.id;
				}
			}
		}
	}
}
